import http from '../httpConfig';
import { API_ENDPOINTS } from '../utils/apiConstant';

export type SubscriptionStateFilter = 'active' | 'expiring_soon' | 'expired' | 'trial';

export interface TenantHealth {
  tenantStatus: {
    statusId: number;
    statusName: string;
    statusColour: string;
  };
  subscription: {
    hasSubscription: boolean;
    endDate: string | null;
    daysUntilExpiry: number | null;
    cancelled: boolean;
  } | null;
  lastActivityAt: string | null;
  configIssueCount: number;
  provisioning: {
    status: 'queued' | 'running' | 'success' | 'failed' | null;
    errorMessage: string | null;
    latestJobId: number | null;
  };
}

export interface TenantListPayload {
  includeDeleted?: boolean;
  showArchived?: boolean;
  page?: number;
  limit?: number;
  productId?: number;
  statusId?: number;
  // US-035: multi-select filters + search
  status?: number[];
  product?: number[];
  plan?: number[];
  country?: number[];
  subscriptionState?: SubscriptionStateFilter[];
  search?: string;
}

export interface BulkSuspendPayload {
  tenantIds?: number[];
  filterParams?: Record<string, unknown>;
  reason: string;
}

export interface BulkActionResult {
  bulkActionId: string;
  succeeded: number[];
  skipped: { id: number; reason: string }[];
  failed: { id: number; reason: string }[];
}

export interface TenantFormPayload {
  tenant_code?: string;
  tenant_name: string;
  slug: string;
  product_id: number;
  owner_name: string;
  owner_email: string;
  owner_phone?: string;
  country_id: number;
  timezone_id?: number;
  category_id?: number;
  isolation_mode?: 'shared' | 'dedicated';
  plan_id: number;
  subscription_start_date: string;
  address_notes?: string;
}

const buildQuery = (params: Record<string, unknown>) => {
  const query: Record<string, string> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') query[key] = String(value);
  });
  const qs = new URLSearchParams(query).toString();
  return qs ? `?${qs}` : '';
};

const list = (payload: TenantListPayload) => {
  const { includeDeleted, showArchived, page, limit, productId, statusId, status, product, plan, country, subscriptionState, search } =
    payload;
  const params: Record<string, string> = {};
  if (includeDeleted) params.includeDeleted = 'true';
  if (showArchived) params.showArchived = 'true';
  if (page) params.page = String(page);
  if (limit) params.limit = String(limit);
  if (productId) params.productId = String(productId);
  if (statusId) params.statusId = String(statusId);
  if (status?.length) params.status = status.join(',');
  if (product?.length) params.product = product.join(',');
  if (plan?.length) params.plan = plan.join(',');
  if (country?.length) params.country = country.join(',');
  if (subscriptionState?.length) params.subscriptionState = subscriptionState.join(',');
  if (search) params.search = search;
  const query = new URLSearchParams(params).toString();
  return http.get(`${API_ENDPOINTS.tenants}${query ? `?${query}` : ''}`);
};

const get = (id: number | string) => http.get(`${API_ENDPOINTS.tenants}/${id}`);

const getHealth = (id: number | string) => http.get(`${API_ENDPOINTS.tenants}/${id}/health`);

const bulkSuspend = (payload: BulkSuspendPayload) => http.post(API_ENDPOINTS.tenantBulkSuspend, payload);

const create = (payload: TenantFormPayload) => http.post(API_ENDPOINTS.tenants, payload);

const update = (id: number | string, payload: Partial<TenantFormPayload>) =>
  http.patch(`${API_ENDPOINTS.tenants}/${id}`, payload);

const checkSlug = (slug: string, excludeId?: number | string) =>
  http.get(`${API_ENDPOINTS.tenantCheckSlug}${buildQuery({ slug, excludeId })}`);

const transitionStatus = (id: number | string, to_status_code: string, reason: string) =>
  http.patch(`${API_ENDPOINTS.tenants}/${id}/status`, { to_status_code, reason });

const checkDependents = (id: number | string) => http.get(`${API_ENDPOINTS.tenants}/${id}/dependents`);

const remove = (id: number | string, reason: string) =>
  http.delete(`${API_ENDPOINTS.tenants}/${id}`, { data: { reason } });

const getTimeline = (id: number | string, page?: number, limit?: number) =>
  http.get(`${API_ENDPOINTS.tenants}/${id}/timeline${buildQuery({ page, limit })}`);

const getProvisioningStatus = (id: number | string) => http.get(`${API_ENDPOINTS.tenants}/${id}/provisioning`);

const getProvisioningHistory = (id: number | string) =>
  http.get(`${API_ENDPOINTS.tenants}/${id}/provisioning/history`);

const retryProvisioning = (id: number | string) => http.post(`${API_ENDPOINTS.tenants}/${id}/provisioning/retry`, {});

const listFailedProvisioning = () => http.get(API_ENDPOINTS.tenantFailedProvisioning);

const getIsolationPromotionCurrent = (id: number | string) =>
  http.get(`${API_ENDPOINTS.tenants}/${id}/isolation-promotion/current`);

const startIsolationPromotion = (id: number | string) =>
  http.post(`${API_ENDPOINTS.tenants}/${id}/isolation-promotion/start`, {});

const cancelIsolationPromotion = (id: number | string) =>
  http.post(`${API_ENDPOINTS.tenants}/${id}/isolation-promotion/cancel`, {});

const getConfig = (id: number | string) => http.get(`${API_ENDPOINTS.tenants}/${id}/config`);

const setConfigValue = (id: number | string, keyId: number | string, value: string) =>
  http.put(`${API_ENDPOINTS.tenants}/${id}/config/${keyId}`, { value });

const getMandatoryCheck = (id: number | string) => http.get(`${API_ENDPOINTS.tenants}/${id}/config/mandatory-check`);

const getSubscriptionHistory = (id: number | string) => http.get(`${API_ENDPOINTS.tenants}/${id}/subscriptions`);

const triggerExport = (id: number | string) => http.post(`${API_ENDPOINTS.tenants}/${id}/export`, {});

const getExportStatus = (id: number | string) => http.get(`${API_ENDPOINTS.tenants}/${id}/export/status`);

const downloadExport = (id: number | string) =>
  http.get(`${API_ENDPOINTS.tenants}/${id}/export/download`, { responseType: 'blob' });

const listPendingPurges = () => http.get(API_ENDPOINTS.tenantPendingPurges);

const setRetentionOverride = (id: number | string, retainUntil: string) =>
  http.put(`${API_ENDPOINTS.tenants}/${id}/retention-override`, { retainUntil });

const removeRetentionOverride = (id: number | string) =>
  http.delete(`${API_ENDPOINTS.tenants}/${id}/retention-override`);

const TenantService = {
  list,
  get,
  getHealth,
  bulkSuspend,
  create,
  update,
  checkSlug,
  transitionStatus,
  checkDependents,
  remove,
  getTimeline,
  getProvisioningStatus,
  getProvisioningHistory,
  retryProvisioning,
  listFailedProvisioning,
  getIsolationPromotionCurrent,
  startIsolationPromotion,
  cancelIsolationPromotion,
  getConfig,
  setConfigValue,
  getMandatoryCheck,
  getSubscriptionHistory,
  triggerExport,
  getExportStatus,
  downloadExport,
  listPendingPurges,
  setRetentionOverride,
  removeRetentionOverride,
};

export default TenantService;
